import React, { useEffect, useMemo, useState } from "react";
import { FlightRequest, FlightDetail, FlightResponse } from "./Types";
import SearchBar from "./SearchBar";
import SortDropdown from "./SortDropdown";
import { getFlightRequests } from "apis/requestServices";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import RequestReviewTab from "./RequestReviewTab";

export const AnsweredRequest: React.FC = () => {
  const [selectedFlight, setSelectedFlight] = useState<FlightRequest | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState<string>("DepartureTime");
  const [searchQuery, setSearchQuery] = useState("");
  const { showLoader, hideLoader } = useGlobalLoader();
  const [flightRequests, setFlightRequests] = useState<FlightRequest[]>([]);
  const [flightResponse, setFlightResponse] = useState<any>();
  const [selectedFlightDetail, setSelectedFlightDetail] =
    useState<FlightDetail | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const status = "Answered";
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(flightResponse?.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const totalPages = Math.ceil(flightResponse?.totalCount / pageSize);

    // If 5 or fewer pages, show all pages
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always include current page, and try to have 2 pages before and after
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    // Adjust if we're at the end
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  // Map display names to API sortBy values
  const mapSortByToApiValue = (displayName: string): string => {
    switch (displayName) {
      case "Departure Time":
        return "DepartureTime";
      case "Flight Number":
        return "FlightNumber";
      case "Route":
        return "Route";
      default:
        return "DepartureTime";
    }
  };

  // Reset currentPage to 1 when sortBy changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  useEffect(() => {
    const fetchData = async () => {
      showLoader();
      try {
        const response = await getFlightRequests({
          pageNumber: currentPage,
          pageSize,
          status,
          sortBy: sortBy,
        });
        setFlightResponse(response);
        setFlightRequests(
          response.items.map((r) => ({
            ...r,
            departureDate: `${new Date(r.departure).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} - ${new Date(r.departure).toLocaleDateString("en-US", { weekday: "short" })}`,
            // departureTime: new Date(r.departure).toLocaleTimeString([], {
            //   hour: "2-digit",
            //   minute: "2-digit",
            //   hourCycle: "h23",
            // }),
            // arrivalTime: new Date(r.arrival).toLocaleTimeString([], {
            //   hour: "2-digit",
            //   minute: "2-digit",
            //   hourCycle: "h23",
            // }),
            // duration:
            //   (
            //     (new Date(r.arrival).getTime() -
            //       new Date(r.departure).getTime()) /
            //     3600000
            //   ).toFixed(1) + "h",
             departureTime: r.departure
            ? r.departure.split("T")[1].slice(0, 5)
            : "",

          arrivalTime: r.arrival
            ? r.arrival.split("T")[1].slice(0, 5)
            : "",
             duration: r.elptim
          ? `${parseInt(r.elptim.slice(0, -2))}h ${parseInt(
              r.elptim.slice(-2)
            )}m`
          : "",
          }))
        );
      } catch (error) {
        console.error("Error fetching flight requests:", error);
      } finally {
        setTimeout(() => hideLoader(), 500);
      }
    };
    fetchData();
  }, [currentPage, sortBy]);

  const handleCardClick = (flight: FlightRequest) => {
    const flightDetail: FlightDetail = {
      logoImg: flight.airlineLogo,
      airlineId: flight.airlineId,
      airlineName: flight.airlineName,
      flightNumber: flight.flightNumber,
      departureAirport: flight.sourceCode,
      arrivalAirport: flight.destinationCode,
      departureTime: flight.departureTime,
      arrivalTime: flight.arrivalTime,
      flightDuration: flight.duration,
      requestDetailId: flight.requestDetailId,
      flightDate: new Date(flight.flightDate)
        .toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          weekday: "short",
        })
        .toUpperCase()
        .replace(/^(\w+),\s(\w+)\s(\d+)$/, "$2 $3 - $1"),
    };
    setSelectedFlight(flight);
    setSelectedFlightDetail(flightDetail);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFlight(null);
  };

  const filteredFlights = useMemo(() => {
    if (!Array.isArray(flightRequests)) return [];
    const result = flightRequests
      .filter((flight) => flight.status === "Answered")
      .filter(
        (flight) =>
          flight.airlineName
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          flight.flightNumber.toLowerCase().includes(searchQuery.toLowerCase())
      );
    // Client-side sorting removed - API handles sorting via sortBy parameter
    return result;
  }, [flightRequests, searchQuery]);

  return (
    <section className="mt-0 answered">
      <div className="flex flex-wrap gap-4 items-center justify-between pb-4 mb-2">
        <p className="text-[#1F2937] text-base font-semibold flex items-center gap-2">
          Answered Requests
          <span className="flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded-full bg-[#FBBF24] text-white">
            {filteredFlights.length}
          </span>
        </p>
        <div className="flex items-center gap-3">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
          <SortDropdown
            currentSortBy={sortBy}
            onSortChange={(displayName) => {
              const apiValue = mapSortByToApiValue(displayName);
              setSortBy(apiValue);
            }}
          />
        </div>
      </div>
      <div>
        {filteredFlights.length === 0 ? (
          <div className="flex justify-center items-center h-40 w-full text-[#9CA3AF]">
            <p>No answered requests found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFlights.map((flight: any, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-4 items-center px-5 py-4 bg-[#F8F9FC] border border-[#EDEEF1] rounded-xl cursor-pointer hover:bg-[#F0F1F5] transition-colors"
                onClick={() => handleCardClick(flight)}
              >
                {/* Airline */}
                <div className="flex items-center lg:col-span-2 md:col-span-4 col-span-12 gap-3">
                  <img
                    className="w-10 h-10 rounded-lg object-contain"
                    src={flight.airlineLogo}
                    alt={flight.airlineName.toLowerCase()}
                  />
                  <div>
                    <p className="text-[#1F2937] text-sm font-semibold">{flight.airlineName}</p>
                    <p className="text-[#9CA3AF] text-xs">{flight?.flightNumber}</p>
                  </div>
                </div>

                {/* Date */}
                <div className="flex items-center lg:col-start-4 lg:col-span-4 md:col-span-8 col-span-12 gap-6">
                  <div>
                    <p className="text-[#9CA3AF] text-[11px]">Date</p>
                    <p className="text-[#1F2937] text-sm font-medium">
                      {(() => {
                        const date = new Date(flight?.flightDate);
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = date.toLocaleString("en-US", { month: "long" });
                        const year = date.getFullYear();
                        return `${day} ${month}, ${year}`;
                      })()}
                    </p>
                  </div>
                </div>

                {/* Route */}
                <div className="flex items-center lg:col-span-5 md:col-span-12 col-span-12 gap-3">
                  <div className="flex flex-col items-start gap-0.5 min-w-[50px]">
                    <p className="text-[#1F2937] text-sm font-semibold">{flight.sourceCode}</p>
                    <p className="text-[#9CA3AF] text-[11px]">{flight.departureTime}</p>
                  </div>

                  <div className="flex flex-col items-center w-full">
                    <div className="flex items-center w-full">
                      <div className="w-2 h-2 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                      <div className="flex-1 border-b border-dashed border-[#C5CAD4] mx-1"></div>
                      <i className="st-flight-slim text-[#737C8C] text-sm mx-1"></i>
                      <div className="flex-1 border-b border-dashed border-[#C5CAD4] mx-1"></div>
                      <div className="w-2 h-2 bg-[#C5CAD4] rounded-full flex-shrink-0"></div>
                    </div>
                    <p className="text-[#9CA3AF] text-[10px] mt-0.5">{flight.duration}</p>
                  </div>

                  <div className="flex flex-col items-end gap-0.5 min-w-[50px]">
                    <p className="text-[#1F2937] text-sm font-semibold">{flight.destinationCode}</p>
                    <p className="text-[#9CA3AF] text-[11px]">{flight.arrivalTime}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {selectedFlightDetail && (
        <RequestReviewTab
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          airlineId={Number(selectedFlightDetail.airlineId)}
          airlineName={selectedFlightDetail.airlineName}
          flightDate={selectedFlightDetail.flightDate}
          departureAirport={selectedFlightDetail.departureAirport}
          flightNumber={selectedFlightDetail.flightNumber}
          departureTime={selectedFlightDetail.departureTime}
          flightDuration={selectedFlightDetail.flightDuration}
          arrivalAirport={selectedFlightDetail.arrivalAirport}
          arrivalTime={selectedFlightDetail.arrivalTime}
          requestDetailId={selectedFlightDetail.requestDetailId}
          logoImg={selectedFlightDetail.logoImg}
        />
      )}

      <section>
        <nav>
          <ul className="flex items-center justify-center my-6 gap-3 text-sm">
            <li>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`flex items-center justify-center w-8 h-8 leading-tight ${
                  currentPage === 1
                    ? "text-gray-300 border border-gray-300 cursor-not-allowed"
                    : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
                } rounded-md`}
              >
                <span className="sr-only">Previous</span>
                <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
              </button>
            </li>

            {getPageNumbers().map((pageNumber) => (
              <li key={pageNumber}>
                <button
                  onClick={() => handlePageChange(pageNumber)}
                  className={`flex items-center justify-center w-8 h-8 rounded-md ${
                    currentPage === pageNumber
                      ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                      : "text-primaryblue-50 border border-primaryblue-50"
                  }`}
                >
                  {pageNumber}
                </button>
              </li>
            ))}

            <li>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={
                  currentPage >=
                  Math.ceil(flightResponse?.totalCount / pageSize)
                }
                className={`flex items-center justify-center w-8 h-8 ${
                  currentPage >=
                  Math.ceil(flightResponse?.totalCount / pageSize)
                    ? "text-gray-300 border border-gray-300 cursor-not-allowed"
                    : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
                } rounded-md`}
              >
                <span className="sr-only">Next</span>
                <i className="st-arrow-right text-xl font-semibold"></i>
              </button>
            </li>
          </ul>
        </nav>
      </section>
    </section>
  );
};

export default AnsweredRequest;
